import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import CodeMirror from '@uiw/react-codemirror';
import { python } from '@codemirror/lang-python';
import { json as jsonLanguage } from '@codemirror/lang-json';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import MemoryIcon from '@mui/icons-material/Memory';
import CloseIcon from '@mui/icons-material/Close';

const PluginEditorDialog = ({
  open,
  onClose,
  t,
  selectedFolder,
  pluginKind,
  pluginType,
  manifestFile,
  scriptFile,
  loadingFiles,
  pluginJson,
  pluginPy,
  onPluginJsonChange,
  onPluginPyChange,
  hasMemory,
  deleting,
  saving,
  onOpenMemory,
  onOpenDelete,
  onSave
}) => {
  const [activeTab, setActiveTab] = useState('json');

  useEffect(() => {
    if (open) {
      setActiveTab('json');
    }
  }, [open, selectedFolder]);

  const identityLabel = pluginType || selectedFolder;
  const handleDialogClose = (_event, reason) => {
    if (reason === 'backdropClick') {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          width: 'calc(100vw - 12px)',
          maxWidth: 'min(1800px, calc(100vw - 12px))',
          height: 'calc(100vh - 12px)',
          maxHeight: 'calc(100vh - 12px)',
          m: '6px',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {t('global_setting.plugins_edit_title', 'Plugin files')}
            </Typography>
            <IconButton
              size="small"
              onClick={onClose}
              disabled={saving || deleting}
              aria-label={t('close', 'Close')}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {identityLabel ? <Chip label={identityLabel} size="small" color="primary" /> : null}
            {pluginKind ? <Chip label={pluginKind} size="small" variant="outlined" /> : null}
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: 2, pb: 1, flex: 1, display: 'flex' }}>
        {loadingFiles ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={1.25} sx={{ mt: 0.25, width: '100%' }}>
            <Tabs
              value={activeTab}
              onChange={(_event, value) => setActiveTab(value)}
              sx={{ minHeight: 40 }}
            >
              <Tab
                value="json"
                label={manifestFile || t('global_setting.plugins_json_label', 'plugin.json')}
              />
              <Tab
                value="python"
                label={scriptFile || t('global_setting.plugins_python_label', 'plugin.py')}
              />
            </Tabs>

            {activeTab === 'json' ? (
              <Box sx={{ border: '1px solid #d0d7de', borderRadius: 1, overflow: 'hidden' }}>
                <CodeMirror
                  value={pluginJson || ''}
                  height="calc(100vh - 180px)"
                  extensions={[jsonLanguage()]}
                  basicSetup={{
                    lineNumbers: true,
                    foldGutter: true,
                    highlightActiveLine: true
                  }}
                  onChange={(value) => onPluginJsonChange(value)}
                />
              </Box>
            ) : (
              <Box sx={{ border: '1px solid #d0d7de', borderRadius: 1, overflow: 'hidden' }}>
                <CodeMirror
                  value={pluginPy || ''}
                  height="calc(100vh - 180px)"
                  extensions={[python()]}
                  basicSetup={{
                    lineNumbers: true,
                    foldGutter: true,
                    highlightActiveLine: true
                  }}
                  onChange={(value) => onPluginPyChange(value)}
                />
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button
          variant="outlined"
          startIcon={<MemoryIcon />}
          disabled={!hasMemory || loadingFiles || deleting}
          onClick={onOpenMemory}
        >
          {t('global_setting.plugins_view_memory', 'View memory')}
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          disabled={deleting || loadingFiles || saving}
          onClick={onOpenDelete}
        >
          {deleting
            ? t('global_setting.plugins_deleting', 'Deleting...')
            : t('global_setting.plugins_delete', 'Delete')}
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveIcon />}
          disabled={saving || loadingFiles || deleting}
          onClick={onSave}
        >
          {saving
            ? t('global_setting.plugins_saving', 'Saving...')
            : t('global_setting.plugins_save', 'Save')}
        </Button>
        <Button disabled={saving || deleting} onClick={onClose}>
          {t('global_setting.cancel', 'Cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PluginEditorDialog;
